const STORAGE_KEY = 'reap-sso:last-state';

export const DEFAULT_POST_LOGIN_REDIRECT_PATH = '/app/overview';

interface SsoRelayState {
  nonce: string;
  redirectPath?: string | null;
  createdAt: number;
}

export interface StoredSsoState {
  encoded: string;
  value: SsoRelayState;
}

const getSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch (error) {
    console.warn('[sso] sessionStorage unavailable', error);
    return null;
  }
};

const createNonce = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

const encodeState = (value: SsoRelayState): string => {
  const json = JSON.stringify(value);
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    return window.btoa(json);
  }
  if (
    typeof globalThis !== 'undefined' &&
    typeof (globalThis as unknown as { Buffer?: typeof Buffer }).Buffer === 'function'
  ) {
    const globalBuffer = (globalThis as unknown as { Buffer: typeof Buffer }).Buffer;
    return globalBuffer.from(json, 'utf8').toString('base64');
  }
  throw new Error('Base64 encoding is not supported in this environment');
};

const decodeStateInternal = (encoded: string): SsoRelayState | null => {
  if (!encoded) {
    return null;
  }
  try {
    let json: string;
    if (typeof window !== 'undefined' && typeof window.atob === 'function') {
      json = window.atob(encoded);
    } else {
      const globalBuffer = (globalThis as unknown as { Buffer?: typeof Buffer }).Buffer;
      if (typeof globalBuffer === 'function') {
        json = globalBuffer.from(encoded, 'base64').toString('utf8');
      } else {
        throw new Error('Base64 decoding is not supported in this environment');
      }
    }
    const value = JSON.parse(json) as SsoRelayState;
    if (!value || typeof value !== 'object' || typeof value.nonce !== 'string') {
      return null;
    }
    return value;
  } catch (error) {
    console.warn('[sso] failed to decode relay state', error);
    return null;
  }
};

export const createSsoRelayState = (redirectPath?: string | null): StoredSsoState => {
  const value: SsoRelayState = {
    nonce: createNonce(),
    redirectPath: redirectPath ?? DEFAULT_POST_LOGIN_REDIRECT_PATH,
    createdAt: Date.now()
  };
  const encoded = encodeState(value);
  const storage = getSessionStorage();
  if (storage) {
    storage.setItem(STORAGE_KEY, JSON.stringify({ encoded, value } satisfies StoredSsoState));
  }
  return { encoded, value };
};

export const readStoredSsoState = (): StoredSsoState | null => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as StoredSsoState;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.encoded !== 'string') {
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn('[sso] failed to parse stored relay state', error);
    storage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const clearStoredSsoState = (): void => {
  const storage = getSessionStorage();
  storage?.removeItem(STORAGE_KEY);
};

export const decodeSsoState = (encoded: string): SsoRelayState | null => {
  return decodeStateInternal(encoded);
};

export const ensureSafeRedirectPath = (value?: string | null): string => {
  if (!value) {
    return DEFAULT_POST_LOGIN_REDIRECT_PATH;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return DEFAULT_POST_LOGIN_REDIRECT_PATH;
  }
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) {
    return DEFAULT_POST_LOGIN_REDIRECT_PATH;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(trimmed);
        if (url.origin === window.location.origin) {
          return `${url.pathname}${url.search}${url.hash}` || DEFAULT_POST_LOGIN_REDIRECT_PATH;
        }
      } catch (error) {
        console.warn('[sso] invalid absolute redirect url', error);
      }
    }
    return DEFAULT_POST_LOGIN_REDIRECT_PATH;
  }
  if (trimmed.includes('\\') || trimmed.startsWith('//')) {
    return DEFAULT_POST_LOGIN_REDIRECT_PATH;
  }
  return trimmed.startsWith('/') ? trimmed : DEFAULT_POST_LOGIN_REDIRECT_PATH;
};
